import React, { useEffect, useState, useRef } from "react";
import L from "leaflet";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { BiArrowFromLeft, BiArrowFromRight, BiEdit } from "react-icons/bi";
import { FiSave } from "react-icons/fi";
import { IoMail } from "react-icons/io5";
import { LuPhone } from "react-icons/lu";
import { TbWorld } from "react-icons/tb";
import { IoLocationSharp } from "react-icons/io5";
import { RiDeleteBin6Line, RiLinkM } from "react-icons/ri";
import {
  MdOutlineCancel,
  MdArrowUpward,
  MdArrowDownward,
} from "react-icons/md";
import { MdSearch } from "react-icons/md";
import { FaRegCopy, FaRegLemon, FaWpforms } from "react-icons/fa";
// react-leaflet replaced with vanilla Leaflet (L) to fix zoom animation errors
import user from "../../Assets/photo/user.png";
import SwitchToggle from "../Common/SwitchToggle";
import axiosUserInstance from "../../axiosUserInstance";
import { useLoader } from "../../context/LoaderContext";
import { hexToRgba, uploadFileToS3 } from "../../utils/common";
import { HiShoppingBag } from "react-icons/hi2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/free-solid-svg-icons";
import { FaImages } from "react-icons/fa";
import { AiOutlinePlus } from "react-icons/ai";

function LocationSearch({ setLat, setLon, setLabel }) {
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debounceTimer = useRef(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (value.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);

    debounceTimer.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json&limit=8`
        );
        const data = await response.json();
        setSearchResults(data);
        setShowResults(true);
      } catch (error) {
        console.error("Error searching locations:", error);
        toast.error("Failed to search locations");
      } finally {
        setIsSearching(false);
      }
    }, 500);
  };

  const handleSelectLocation = (result) => {
    setLat(parseFloat(result.lat));
    setLon(parseFloat(result.lon));
    setLabel(result.display_name);
    setSearchInput(result.display_name);
    setShowResults(false);
  };

  return (
    <div className="position-relative mb-3">
      <div className="d-flex align-items-center gap-2">
        <div className="position-relative w-100">
          <input
            type="text"
            placeholder="Search for a location..."
            value={searchInput}
            onChange={handleSearchChange}
            className="form-control"
            style={{
              paddingLeft: "35px",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          />
          <MdSearch
            style={{
              position: "absolute",
              left: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#999",
              fontSize: "18px",
            }}
          />
          {isSearching && (
            <div
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <span className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </span>
            </div>
          )}
        </div>
      </div>

      {showResults && searchResults.length > 0 && (
        <div
          className="position-absolute w-100 bg-white border rounded mt-1"
          style={{
            maxHeight: "250px",
            overflowY: "auto",
            zIndex: 1000,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          {searchResults.map((result, index) => (
            <div
              key={index}
              onClick={() => handleSelectLocation(result)}
              className="p-2 border-bottom"
              style={{
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <div style={{ fontSize: "14px", fontWeight: "500" }}>
                {result.name || result.display_name.split(",")[0]}
              </div>
              <div
                style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}
              >
                {result.display_name}
              </div>
            </div>
          ))}
        </div>
      )}

      {showResults &&
        searchResults.length === 0 &&
        searchInput.trim().length >= 2 && (
          <div
            className="position-absolute w-100 bg-white border rounded mt-1 p-3 text-center"
            style={{
              zIndex: 1000,
              color: "#999",
            }}
          >
            No locations found
          </div>
        )}
    </div>
  );
}

const icon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
  iconSize: [32, 32],
});

// Isolated static map using vanilla Leaflet with full event blocking
function DisplayMap({ lat, lon }) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const container = containerRef.current;
    if (!wrapper || !container) return;

    // Block ALL wheel, touch, pointer, and mouse events at the DOM level
    // before they can reach Leaflet internals. This is the only reliable way
    // to prevent _performZoom from firing when the map is inside a scroll container.
    const blockEvent = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const eventsToBlock = ["wheel", "mousewheel", "DOMMouseScroll", "dblclick"];
    eventsToBlock.forEach((evt) => {
      wrapper.addEventListener(evt, blockEvent, {
        passive: false,
        capture: true,
      });
    });

    // Clear any stale Leaflet instance
    if (container._leaflet_id) {
      delete container._leaflet_id;
    }

    const map = L.map(container, {
      center: [lat, lon],
      zoom: 14,
      dragging: true,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      boxZoom: false,
      keyboard: false,
      tap: false,
      zoomControl: false,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
      inertia: false,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: false,
    }).addTo(map);
    L.marker([lat, lon], { icon }).addTo(map);

    mapRef.current = map;

    return () => {
      // Remove DOM event blockers
      eventsToBlock.forEach((evt) => {
        wrapper.removeEventListener(evt, blockEvent, { capture: true });
      });

      if (mapRef.current) {
        mapRef.current.off();
        try {
          mapRef.current.remove();
        } catch (e) {
          // suppress
        }
        mapRef.current = null;
      }
    };
  }, [lat, lon]);

  // Outer wrapper intercepts events; inner div is the Leaflet container
  return (
    <div
      ref={wrapperRef}
      style={{ width: "100%", height: "350px", overflow: "hidden" }}
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

// Interactive map for the edit form — uses vanilla Leaflet to avoid react-leaflet zoom errors
function EditMap({ lat, lon, onMove }) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (container._leaflet_id) {
      delete container._leaflet_id;
    }

    const map = L.map(container, {
      center: [lat, lon],
      zoom: 14,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
      map
    );

    map.on("move", () => {
      try {
        const c = map.getCenter();
        if (c) onMove(c.lat, c.lng);
      } catch (e) {}
    });

    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.off();
        try {
          mapRef.current.remove();
        } catch (e) {}
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only init once — lat/lon changes handled by map.setView below

  // Sync external lat/lon changes (e.g. from location search) into the map
  useEffect(() => {
    if (mapRef.current && lat && lon) {
      try {
        mapRef.current.setView([lat, lon], mapRef.current.getZoom(), {
          animate: false,
        });
      } catch (e) {}
    }
  }, [lat, lon]);

  return (
    <div
      ref={wrapperRef}
      style={{ width: "100%", height: "100%", position: "relative" }}
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

const WidgetComponent = ({
  userData,
  userWidget,
  setUserData,
  handleOpenQueueModal,
  handleEditSocialClick,
  handleOpenEmbedAlbumModal,
  openSocialLinkThemeModal,
  openFormThemeModal,
  openAddForm,
  openAlbumsWidgetSheet,
}) => {
  const { setLoading } = useLoader();
  const [widgets, setWidgets] = useState(userWidget?.widgets || []);
  const [eventPoster, setEventPoster] = useState(
    userWidget?.eventPoster || null
  );
  const [preview, setPreview] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [editingWidgetId, setEditingWidgetId] = useState(null);
  const [editingWidget, setEditingWidget] = useState(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    setPreview(eventPoster?.url || null);
    if (eventPoster?.type === "image") {
      setPreviewType("image");
    } else if (eventPoster?.type === "video") {
      setPreviewType("video/mp4");
    }
    setWidgets(userWidget?.widgets || []);
    setEventPoster(userWidget?.eventPoster || null);
  }, [userWidget]);

  // Handle widget delete
  const handleDeleteWidget = async (_id) => {
    const widgetToDelete = widgets.find((w) => w._id === _id);
    const isFormWidget = widgetToDelete?.type === "form";

    const confirmed = await Swal.fire({
      title: "Delete Widget",
      text: "Are you sure you want to delete this widget?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      ...(isFormWidget && {
        footer:
          '<span style="color: #dc3545; font-size: 13px;">⚠️ The form records will also be deleted for this widget.</span>',
      }),
    });

    if (!confirmed.isConfirmed) return;

    try {
      setLoading(true);
      const response = await axiosUserInstance.delete(`/widget/delete/${_id}`);
      if (response?.status === 200) {
        const data = response.data?.data;
        const links =
          data?.widgets
            .filter((w) => w.type === "socialLink")
            .map((w) => ({ _id: w._id, ...w.socialLink })) || [];
        setUserData((prev) => ({
          ...prev,
          userWidget: data,
          socialLinks: links,
        }));
        toast.success("Widget deleted successfully.");
      } else {
        toast.error(response?.message || "Failed to delete widget.");
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message || err?.message || "An error occurred.";
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  // Handle widget reorder (up/down)
  const handleReorderWidget = async (_id, direction) => {
    try {
      setLoading(true);
      const response = await axiosUserInstance.post(`/widget/reorder`, {
        _id,
        direction,
      });
      if (response?.status === 200) {
        const data = response.data?.data;
        const links =
          data?.widgets
            .filter((w) => w.type === "socialLink")
            .map((w) => ({ _id: w._id, ...w.socialLink })) || [];
        setUserData((prev) => ({
          ...prev,
          userWidget: data,
          socialLinks: links,
        }));
        toast.success(`Widget moved ${direction} successfully.`);
      } else {
        toast.error(response?.message || "Failed to reorder widget.");
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message || err?.message || "An error occurred.";
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  // Handle widget copy
  const handleCopyWidget = async (_id) => {
    try {
      setLoading(true);
      const response = await axiosUserInstance.post(`/widget/copy`, {
        _id,
      });
      if (response?.status === 200) {
        const data = response.data?.data;
        const links =
          data?.widgets
            .filter((w) => w.type === "socialLink")
            .map((w) => ({ _id: w._id, ...w.socialLink })) || [];
        setUserData((prev) => ({
          ...prev,
          userWidget: data,
          socialLinks: links,
        }));
        toast.success("Widget copied successfully.");
      } else {
        toast.error(response?.message || "Failed to copy widget.");
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message || err?.message || "An error occurred.";
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit widget
  const handleEditWidget = (widget) => {
    setEditingWidgetId(widget._id);
    setEditingWidget({ ...widget });
  };

  // Handle save widget
  const handleSaveWidget = async () => {
    if (!editingWidget) return;

    try {
      setLoading(true);
      const response = await axiosUserInstance.post(`/widget/edit`, {
        _id: editingWidget._id,
        ...editingWidget,
      });
      if (response?.status === 200) {
        const data = response.data?.data;
        const links =
          data?.widgets
            .filter((w) => w.type === "socialLink")
            .map((w) => ({ _id: w._id, ...w.socialLink })) || [];
        setUserData((prev) => ({
          ...prev,
          userWidget: data,
          socialLinks: links,
        }));
        setEditingWidgetId(null);
        setEditingWidget(null);
        toast.success("Widget updated successfully.");
      } else {
        toast.error(response?.message || "Failed to update widget.");
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message || err?.message || "An error occurred.";
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingWidgetId(null);
    setEditingWidget(null);
  };

  // Handle poster delete
  const handleDeletePoster = async () => {
    const confirmed = await Swal.fire({
      title: "Delete Poster",
      text: "Are you sure you want to delete the poster?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (!confirmed.isConfirmed) return;

    try {
      setLoading(true);
      const response = await axiosUserInstance.delete(`/widget/delete-poster`);
      if (response?.status === 200) {
        const data = response.data?.data;
        const links =
          data?.widgets
            .filter((w) => w.type === "socialLink")
            .map((w) => ({ _id: w._id, ...w.socialLink })) || [];
        setUserData((prev) => ({
          ...prev,
          userWidget: data,
          socialLinks: links,
        }));
        toast.success("Poster deleted successfully.");
      } else {
        toast.error(response?.message || "Failed to delete poster.");
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message || err?.message || "An error occurred.";
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  // Handle poster upload
  const handlePosterUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const url = URL.createObjectURL(file);
      setPreview(url);
      setPreviewType(file.type.startsWith("image") ? "image" : "video");

      const fileURL = await uploadFileToS3(file);

      // Update local state
      const updatedPoster = {
        ...eventPoster,
        type: file.type.startsWith("image") ? "image" : "video",
        url: fileURL,
      };
      setEventPoster(updatedPoster);

      // Save to backend
      const response = await axiosUserInstance.post(`/widget/update-poster`, {
        type: updatedPoster.type,
        url: updatedPoster.url,
        isShow: updatedPoster.isShow,
        isShowToEditProfile: updatedPoster.isShowToEditProfile,
      });

      if (response?.status === 200) {
        const data = response.data?.data;
        const links =
          data?.widgets
            .filter((w) => w.type === "socialLink")
            .map((w) => ({ _id: w._id, ...w.socialLink })) || [];
        setUserData((prev) => ({
          ...prev,
          userWidget: data,
          socialLinks: links,
        }));
        toast.success("Poster uploaded successfully.");
      }
    } catch (error) {
      toast.error("Failed to upload poster.");
    } finally {
      setLoading(false);
    }
  };

  // Handle toggle isShow
  const handleToggleIsShow = async (e) => {
    try {
      setLoading(true);
      const updatedPoster = {
        ...eventPoster,
        isShow: e.target.checked,
      };
      setEventPoster(updatedPoster);

      const response = await axiosUserInstance.post(`/widget/update-poster`, {
        isShow: e.target.checked,
      });

      if (response?.status === 200) {
        const data = response.data?.data;
        const links =
          data?.widgets
            .filter((w) => w.type === "socialLink")
            .map((w) => ({ _id: w._id, ...w.socialLink })) || [];
        setUserData((prev) => ({
          ...prev,
          userWidget: data,
          socialLinks: links,
        }));
        toast.success(
          `Poster ${e.target.checked ? "enabled" : "disabled"} on public page.`
        );
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message || err?.message || "An error occurred.";
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  // Render widget based on type
  const renderWidgetRow = (widget) => {
    const isEditing = editingWidgetId === widget._id;
    return (
      <div key={widget._id} className="mb-4 border rounded p-2">
        <div className="d-flex justify-content-between align-items-center mb-3 render-ridget-row">
          <div className="text-start">
            <h5
              className="mb-0"
              style={{ fontFamily: userData?.pageTheme?.pageFont }}
            >
              {widget.type === "albums"
                ? "Albums"
                : widget.type === "albumEmbed"
                  ? "Panels"
                  : widget.type.charAt(0).toUpperCase() + widget.type.slice(1)}
            </h5>
            <small
              className=""
              style={{ fontFamily: userData?.pageTheme?.pageFont }}
            >
              Order: {widget.order}
            </small>
          </div>

          {!isEditing ? (
            <div className="d-flex gap-2">
              {widget?.type === "albums" && (
                <button
                  className="btn btn-icon"
                  type="button"
                  onClick={() => openAlbumsWidgetSheet?.(widget)}
                  title="Add/Select albums"
                  style={{
                    borderRadius: `${userData?.pageTheme?.backgroundRadius}px`,
                  }}
                >
                  <AiOutlinePlus size={16} />
                </button>
              )}
              {widget?.type === "socialLink" && (
                <button
                  className="btn btn-icon"
                  type="button"
                  onClick={() => {
                    openSocialLinkThemeModal();
                  }}
                  title="SocialLink theme"
                  style={{
                    borderRadius: `${userData?.pageTheme?.backgroundRadius}px`,
                  }}
                >
                  <FontAwesomeIcon icon={faLink} />
                </button>
              )}
              {widget?.type === "form" && (
                <button
                  className="btn btn-icon"
                  type="button"
                  onClick={() => openFormThemeModal?.()}
                  title="Form theme"
                  style={{
                    borderRadius: `${userData?.pageTheme?.backgroundRadius}px`,
                  }}
                >
                  <FaWpforms size={14} />
                </button>
              )}
              {!["form", "albums"].includes(widget.type) && (
                <button
                  className="btn btn-icon"
                  onClick={() =>
                    widget.type === "socialLink"
                      ? handleEditSocialClick({
                          ...widget?.socialLink,
                          _id: widget._id,
                        })
                      : widget.type === "albumEmbed"
                        ? handleOpenEmbedAlbumModal(widget)
                        : widget.type === "queue"
                          ? handleOpenQueueModal(widget)
                          : handleEditWidget(widget)
                  }
                  title="Edit widget"
                  style={{
                    borderRadius: `${userData?.pageTheme?.backgroundRadius}px`,
                  }}
                >
                  <BiEdit size={16} />
                </button>
              )}
              <button
                className="btn btn-icon"
                onClick={() => handleCopyWidget(widget._id)}
                title="Copy widget"
                style={{
                  borderRadius: `${userData?.pageTheme?.backgroundRadius}px`,
                }}
              >
                <FaRegCopy size={16} />
              </button>
              <button
                className="btn btn-icon"
                onClick={() => handleReorderWidget(widget._id, "up")}
                title="Move up"
                disabled={widget.order === 1}
                style={{
                  borderRadius: `${userData?.pageTheme?.backgroundRadius}px`,
                }}
              >
                <MdArrowUpward size={16} />
              </button>
              <button
                className="btn btn-icon"
                onClick={() => handleReorderWidget(widget._id, "down")}
                title="Move down"
                disabled={widget.order === widgets.length}
                style={{
                  borderRadius: `${userData?.pageTheme?.backgroundRadius}px`,
                }}
              >
                <MdArrowDownward size={16} />
              </button>
              <button
                className="btn btn-icon"
                onClick={() => handleDeleteWidget(widget._id)}
                title="Delete widget"
                style={{
                  borderRadius: `${userData?.pageTheme?.backgroundRadius}px`,
                }}
              >
                <RiDeleteBin6Line size={16} />
              </button>
            </div>
          ) : (
            <div className="d-flex gap-2">
              <button
                className="btn btn-icon"
                onClick={handleSaveWidget}
                title="Save widget"
                style={{
                  borderRadius: `${userData?.pageTheme?.backgroundRadius}px`,
                }}
              >
                <FiSave size={16} />
              </button>
              <button
                className="btn btn-icon"
                onClick={handleCancelEdit}
                title="Cancel edit"
                style={{
                  borderRadius: `${userData?.pageTheme?.backgroundRadius}px`,
                }}
              >
                <MdOutlineCancel size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Render widget fields based on type */}
        {isEditing ? (
          <div className="widget-edit-form">
            {widget.type === "card" && (
              <>
                <div className="mb-2">
                  <label style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    value={editingWidget.email || ""}
                    onChange={(e) =>
                      setEditingWidget({
                        ...editingWidget,
                        email: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="mb-2">
                  <label style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                    Number
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingWidget.number || ""}
                    onChange={(e) =>
                      setEditingWidget({
                        ...editingWidget,
                        number: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="mb-2">
                  <label style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                    Website
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingWidget.website || ""}
                    onChange={(e) =>
                      setEditingWidget({
                        ...editingWidget,
                        website: e.target.value,
                      })
                    }
                  />
                </div>
              </>
            )}

            {widget.type === "queue" && (
              <>
                <div className="mb-2">
                  <label style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                    Title
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingWidget.title || ""}
                    onChange={(e) =>
                      setEditingWidget({
                        ...editingWidget,
                        title: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="mb-2">
                  <label style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                    Release Date
                  </label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={editingWidget.releaseAt || ""}
                    onChange={(e) =>
                      setEditingWidget({
                        ...editingWidget,
                        releaseAt: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="mb-2">
                  <label style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                    Description
                  </label>
                  <textarea
                    className="form-control"
                    value={editingWidget.description || ""}
                    onChange={(e) =>
                      setEditingWidget({
                        ...editingWidget,
                        description: e.target.value,
                      })
                    }
                  />
                </div>
              </>
            )}

            {widget.type === "title" && (
              <div className="mb-2">
                <label style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                  Title Text
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editingWidget.text || ""}
                  onChange={(e) =>
                    setEditingWidget({ ...editingWidget, text: e.target.value })
                  }
                />
              </div>
            )}

            {widget.type === "description" && (
              <div className="mb-2">
                <label style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                  Description Text
                </label>
                <textarea
                  className="form-control"
                  value={editingWidget.text || ""}
                  onChange={(e) =>
                    setEditingWidget({ ...editingWidget, text: e.target.value })
                  }
                />
              </div>
            )}

            {widget.type === "note" && (
              <div className="mb-2">
                <label style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                  Note Text
                </label>
                <textarea
                  className="form-control"
                  value={editingWidget.text || ""}
                  onChange={(e) =>
                    setEditingWidget({ ...editingWidget, text: e.target.value })
                  }
                />
              </div>
            )}

            {widget.type === "email" && (
              <div className="mb-2">
                <label style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                  Email
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={editingWidget.text || ""}
                  onChange={(e) =>
                    setEditingWidget({ ...editingWidget, text: e.target.value })
                  }
                />
              </div>
            )}

            {widget.type === "number" && (
              <div className="mb-2">
                <label style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  className="form-control"
                  value={editingWidget.text || ""}
                  onChange={(e) =>
                    setEditingWidget({ ...editingWidget, text: e.target.value })
                  }
                />
              </div>
            )}

            {widget.type === "mapDetails" && isMounted && (
              <>
                <LocationSearch
                  setLat={(lat) =>
                    setEditingWidget((prev) => ({ ...prev, lat }))
                  }
                  setLon={(lon) =>
                    setEditingWidget((prev) => ({ ...prev, lon }))
                  }
                  setLabel={(label) =>
                    setEditingWidget((prev) => ({ ...prev, label }))
                  }
                />
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "350px",
                    marginBottom: "15px",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -100%)",
                      zIndex: 999,
                      pointerEvents: "none",
                      fontSize: "24px",
                    }}
                  >
                    📍
                  </div>

                  <EditMap
                    key={`edit-map-${editingWidgetId}`}
                    lat={
                      editingWidget?.lat && !isNaN(editingWidget.lat)
                        ? editingWidget.lat
                        : 20
                    }
                    lon={
                      editingWidget?.lon && !isNaN(editingWidget.lon)
                        ? editingWidget.lon
                        : 0
                    }
                    onMove={(lat, lon) =>
                      setEditingWidget((prev) => ({ ...prev, lat, lon }))
                    }
                  />
                </div>
                <div className="d-flex align-items-center gap-2 mt-2 justify-content-center">
                  <input
                    type="text"
                    placeholder="Location label"
                    value={editingWidget.label || ""}
                    className="form-control"
                    onChange={(e) =>
                      setEditingWidget((prev) => ({
                        ...prev,
                        label: e.target.value,
                      }))
                    }
                  />
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="widget-content">
            {widget.type === "socialLink" && (
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <a
                  className="social-link"
                  href={widget?.socialLink?.link}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    backgroundColor: hexToRgba(
                      userData?.socialLinkTheme?.background,
                      userData?.socialLinkTheme?.backgroundOpacity
                    ),
                    border: `${userData?.socialLinkTheme?.backgroundBorderWidth}px solid ${userData?.socialLinkTheme?.backgroundBorder}`,
                    borderRadius: `${userData?.socialLinkTheme?.backgroundRadius}px`,
                    color: userData?.socialLinkTheme?.titleColor,
                  }}
                >
                  <img
                    src={widget?.socialLink?.thumbnailUrl || user}
                    alt={widget?.socialLink?.label}
                    className="social-thumbnail"
                    style={{
                      borderRadius: `${userData?.socialLinkTheme?.thumbnailRadius}px`,
                    }}
                  />
                  {widget?.socialLink?.type === "lemon8" ? (
                    <>
                      <FaRegLemon
                        className="social-platform-icon"
                        color={userData?.socialLinkTheme?.iconColor}
                      />
                    </>
                  ) : widget?.socialLink?.type === "shop" ? (
                    <>
                      <HiShoppingBag
                        className="social-platform-icon"
                        color={userData?.socialLinkTheme?.iconColor}
                      />
                    </>
                  ) : (
                    <i
                      className={`social-platform-icon fab fa-${widget?.socialLink?.type}`}
                      style={{
                        color: userData?.socialLinkTheme?.iconColor,
                      }}
                    ></i>
                  )}
                  <div className="social-info">
                    <div
                      className="social-label"
                      style={{
                        color: userData?.socialLinkTheme?.titleColor,
                        fontFamily: userData?.pageTheme?.pageFont,
                      }}
                    >
                      {widget?.socialLink?.label}
                    </div>
                    <div
                      className="social-url"
                      style={{
                        color: userData?.socialLinkTheme?.subtitleColor,
                        fontFamily: userData?.pageTheme?.pageFont,
                      }}
                    >
                      {widget?.socialLink?.link}
                    </div>
                  </div>
                  {/* <button
                    className="btn btn-outline edit-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleEditSocialClick({
                        ...widget?.socialLink,
                        _id: widget._id,
                      });
                    }}
                    style={{
                      backgroundColor:
                        userData?.socialLinkTheme?.buttonBackground,
                      border: `1px solid ${userData?.socialLinkTheme?.buttonBorder}`,
                      borderRadius: `${userData?.socialLinkTheme?.buttonRadius}px`,
                      color: userData?.socialLinkTheme?.buttonTextColor,
                    }}
                  >
                    <BiEdit size={18} />
                  </button> */}
                </a>
              </div>
            )}

            {widget.type === "card" && (
              <div>
                <p
                  className="mb-1"
                  style={{ fontFamily: userData?.pageTheme?.pageFont }}
                >
                  <IoMail /> Email: {widget.email}
                </p>
                <p
                  className="mb-1"
                  style={{ fontFamily: userData?.pageTheme?.pageFont }}
                >
                  <LuPhone /> Number: {widget.number}
                </p>
                <p
                  className="mb-1"
                  style={{ fontFamily: userData?.pageTheme?.pageFont }}
                >
                  <TbWorld /> Website: {widget.website}
                </p>
              </div>
            )}

            {widget.type === "queue" && (
              <div>
                <p
                  className="mb-1"
                  style={{ fontFamily: userData?.pageTheme?.pageFont }}
                >
                  <strong>Title:</strong> {widget.title}
                </p>
                <p
                  className="mb-1"
                  style={{ fontFamily: userData?.pageTheme?.pageFont }}
                >
                  <strong>Release Date:</strong>{" "}
                  {new Date(widget.releaseAt).toLocaleString()}
                </p>
                <p
                  className="mb-1"
                  style={{ fontFamily: userData?.pageTheme?.pageFont }}
                >
                  <strong>Description:</strong> {widget.description}
                </p>
              </div>
            )}

            {["title", "description", "note", "email", "number"].includes(
              widget.type
            ) && (
              <p
                className="mb-1"
                style={{ fontFamily: userData?.pageTheme?.pageFont }}
              >
                {widget.text}
              </p>
            )}

            {widget.type === "mapDetails" && !editingWidgetId && (
              <div className="mb-2 rounded p-2 bg-light">
                <DisplayMap
                  key={`display-${widget._id}`}
                  lat={widget?.lat && !isNaN(widget.lat) ? widget.lat : 20}
                  lon={widget?.lon && !isNaN(widget.lon) ? widget.lon : 0}
                />
                {widget.label && (
                  <div className="d-flex align-items-center justify-content-center mt-2">
                    <IoLocationSharp
                      className="me-1"
                      style={{ color: "#e50027", fontSize: "20px" }}
                    />
                    <p
                      className="mb-0"
                      style={{ fontFamily: userData?.pageTheme?.pageFont }}
                    >
                      {widget.label}
                    </p>
                  </div>
                )}
                {widget.lat && widget.lon && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${widget.lat},${widget.lon}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "block",
                      fontSize: "14px",
                      textAlign: "center",
                      marginTop: "10px",
                      fontFamily: userData?.pageTheme?.pageFont,
                    }}
                  >
                    Open in Google Maps
                  </a>
                )}
              </div>
            )}

            {widget.type === "youtube" && (
              <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                <RiLinkM /> {widget.content}
              </p>
            )}

            {widget.type === "albumEmbed" && (
              <div>
                <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                  <strong>Media Type:</strong> {widget.mediaType}
                </p>
                <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                  <strong>Ratio:</strong> {widget.ratio}
                </p>
              </div>
            )}

            {widget.type === "albums" && (
              <div
                className="d-flex align-items-center justify-content-between p-2 bg-light rounded"
                style={{ cursor: "pointer" }}
                onClick={() => openAlbumsWidgetSheet?.(widget)}
              >
                <div
                  className="d-flex align-items-center gap-2"
                  style={{ width: "100%" }}
                >
                  <FaImages />
                  {(() => {
                    const selectedIds = Array.isArray(widget.albumIds)
                      ? widget.albumIds.map((x) => String(x))
                      : [];
                    const selectedAlbums =
                      selectedIds.length > 0
                        ? (userData?.albums || []).filter((a) =>
                            selectedIds.includes(String(a?._id))
                          )
                        : [];

                    return (
                      <>
                        <div
                          style={{ fontFamily: userData?.pageTheme?.pageFont }}
                        >
                          {selectedAlbums.length > 0
                            ? `${selectedAlbums.length} album(s)`
                            : "Upload & select albums"}
                        </div>
                        {selectedAlbums.length > 0 && (
                          <div className="d-flex align-items-center gap-1 ms-auto">
                            {selectedAlbums.slice(0, 3).map((a) => {
                              const first = a?.media?.[0];
                              if (!first) return null;
                              return first.type?.startsWith("video/") ? (
                                <div
                                  key={a?._id}
                                  style={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: 8,
                                    overflow: "hidden",
                                    background: "#e5e7eb",
                                  }}
                                >
                                  <video
                                    src={first.url}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                    muted
                                    playsInline
                                  />
                                </div>
                              ) : (
                                <img
                                  key={a?._id}
                                  src={first.url}
                                  alt={a?.name || "album"}
                                  style={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: 8,
                                    objectFit: "cover",
                                    background: "#e5e7eb",
                                  }}
                                />
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <AiOutlinePlus />
              </div>
            )}

            {widget.type === "form" && (
              <button
                className="my-form-btn"
                onClick={() => openAddForm(widget)}
                style={{
                  fontFamily: userData?.pageTheme?.pageFont,
                  backgroundColor:
                    userData?.formTheme?.buttonColor || undefined,
                  borderRadius:
                    typeof userData?.formTheme?.borderRadius === "number"
                      ? userData.formTheme.borderRadius
                      : undefined,
                }}
              >
                <FaWpforms size={24} />
                My Form
                <BiArrowFromLeft size={24} />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="text-center">
      {/* Display all widgets */}
      {widgets && widgets.length > 0 ? (
        <div>{widgets.map((widget) => renderWidgetRow(widget))}</div>
      ) : (
        <p
          className="text-muted"
          style={{ fontFamily: userData?.pageTheme?.pageFont }}
        >
          No widgets added yet.
        </p>
      )}

      {/* Event Poster Section */}
      {eventPoster?.isShowToEditProfile && (
        <div className="mb-4 border rounded p-2">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5
              className="mb-0"
              style={{ fontFamily: userData?.pageTheme?.pageFont }}
            >
              Event Poster
            </h5>
            <button
              className="btn btn-icon"
              onClick={handleDeletePoster}
              title="Delete poster"
            >
              <RiDeleteBin6Line size={16} />
            </button>
          </div>

          {preview && (
            <div className="mb-3">
              {previewType === "image" ? (
                <img
                  src={preview}
                  alt="Poster Preview"
                  style={{ maxWidth: "200px", maxHeight: "200px" }}
                  className="mx-auto"
                />
              ) : (
                <video
                  style={{ maxWidth: "200px", maxHeight: "200px" }}
                  controls
                >
                  <source src={preview} type={previewType} />
                </video>
              )}
            </div>
          )}

          <div className="mb-3">
            <input
              type="file"
              accept="image/*,video/*"
              onChange={handlePosterUpload}
              className="form-control"
            />
          </div>

          {/* Toggle switches for isShow and isShowToEditProfile */}
          {eventPoster?.url && (
            <div className="d-flex flex-column gap-2">
              <div className="d-flex align-items-center justify-content-between p-2 bg-light rounded">
                <label className="mb-0">
                  <strong style={{ fontFamily: userData?.pageTheme?.pageFont }}>
                    Show on Public Page
                  </strong>
                </label>
                <SwitchToggle
                  checked={eventPoster?.isShow || false}
                  onChange={handleToggleIsShow}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WidgetComponent;
